import { useMemo } from "react"
import { Box, Card, Typography, useTheme, Stack } from "@mui/material"
import { Link } from "react-router-dom"

import { useTranslation } from "providers/i18n"
import { useHome } from "extensions/em-core/hooks/home/useHome"

import { FullScreenOverlayLayout } from "layouts/FullScreenOverlayLayout"

import { WelcomePanel } from "components/WelcomePanel"
import { Head } from "components/Head"
import { Header, HeaderLogo, HeaderTitle } from "components/Header"
import { Clock } from "components/Clock"
import { Carousel, CarouselItem } from "components/Carousel"
import { HighlightFeatureCard } from "components/domains/events"
import { FullScreenStart } from "components/domains/home"
import { getIcon } from "utils/icon"
import { CalendarStarIcon, PercentIcon } from "components/icons"

export const HomePage = ({ navigations }) => {
  const theme = useTheme()
  const { t } = useTranslation()

  const { featuredEventPromotion, defaultItemsLinks } = useHome()

  const styleOverrides = theme.components[`HomePage`]?.styleOverrides || {}

  const defaultLinkIcons = useMemo(() => {
    return {
      events: getIcon(theme?.assets?.defaultLinkIcon?.events) || (
        <CalendarStarIcon />
      ),
      promotions: getIcon(theme?.assets?.defaultLinkIcon?.promotions) || (
        <PercentIcon />
      ),
    }
  }, [theme])

  const {
    root,
    contentContainer,
    featureCarouselWrapper,
    headerWrapper,
    welcomePanelWrapper,
    textCard,
    linkCardContainer,
    defaultCardPrimaryText,
    defaultCardSecondaryText,
    defaultCardIcon,
  } = styleOverrides

  return (
    <>
      <Head title={t("Indoor map")} />
      <Box sx={headerWrapper}>
        <Header
          logo={<HeaderLogo />}
          title={<HeaderTitle />}
          clock={<Clock />}
        />
      </Box>
      <FullScreenOverlayLayout sx={root}>
        <Box sx={contentContainer}>
          <FullScreenStart />
          <Box sx={welcomePanelWrapper}>
            <WelcomePanel navigations={navigations} />
          </Box>
        </Box>
        <Box sx={featureCarouselWrapper}>
          <Carousel
            options={{
              gap: theme.spacing(1.25),
              pagination: false,
              focus: "center",
              arrows: false,
              mediaQuery: "min",
              breakpoints: {
                640: {
                  perPage: 2,
                },
                900: {
                  perPage: 3,
                },
              },
            }}
          >
            {featuredEventPromotion.map((feature) => {
              const { feature_type, id } = feature
              return (
                <CarouselItem key={id}>
                  {/*
                   * @Example: Event => events/event-id, Promotion => promotions/promotion-id
                   */}
                  <Link to={`${feature_type}s/${id}`}>
                    <HighlightFeatureCard feature={feature} />
                  </Link>
                </CarouselItem>
              )
            })}
            {defaultItemsLinks && defaultItemsLinks.length > 0 ? (
              <CarouselItem>
                <Box sx={linkCardContainer}>
                  {defaultItemsLinks.map(
                    ({ to, primaryText, secondaryText, icon }) => (
                      <Link key={to} to={to}>
                        <Card sx={textCard}>
                          <Stack spacing={1}>
                            <Box sx={defaultCardIcon}>
                              {defaultLinkIcons[icon]}
                            </Box>
                            <Stack spacing={0.5}>
                              <Typography sx={defaultCardPrimaryText}>
                                {t(primaryText)}
                              </Typography>
                              <Typography sx={defaultCardSecondaryText}>
                                {t(secondaryText)}
                              </Typography>
                            </Stack>
                          </Stack>
                        </Card>
                      </Link>
                    )
                  )}
                </Box>
              </CarouselItem>
            ) : null}
          </Carousel>
        </Box>
      </FullScreenOverlayLayout>
    </>
  )
}
